// pages/ai-events/EventGeneratorPage.tsx

import React, { useState } from 'react';
import { useAIEventGeneration } from '@/hooks/useAIEventGeneration';

import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { EventIndustry, EventType } from '@/store/features/ai-events/aiEventsTypes';

const EventGeneratorPage = () => {
  const navigate = useNavigate();
  const {
    generate,
    generatedEvent,
    isGenerating,
    generationError,
    savedEventId,
    clear,
  } = useAIEventGeneration();

  const [prompt, setPrompt] = useState('');
  const [industry, setIndustry] = useState<EventIndustry>(EventIndustry.TECH);
  const [eventType, setEventType] = useState<EventType>(EventType.CONFERENCE);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    try {
      const result = await generate({
        prompt,
        industry,
        event_type: eventType,
      });

      toast.success('Event generated successfully!');
      
      // Optionally navigate to the created event
      if (result.saved_event?.documentId) {
        navigate(`/events/${result.saved_event.documentId}`);
      }
    } catch (error) {
      toast.error('Failed to generate event');
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">AI Event Generator</h1>
        <p className="text-gray-600">
          Describe your event and let AI create it for you
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Describe your event *
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="E.g., Create a tech conference about AI and machine learning in Mumbai for 500 people with networking sessions"
            className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            disabled={isGenerating}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Industry *
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value as EventIndustry)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isGenerating}
            >
              {Object.values(EventIndustry).map((ind) => (
                <option key={ind} value={ind}>
                  {ind.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Event Type *
            </label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value as EventType)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isGenerating}
            >
              {Object.values(EventType).map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={isGenerating || !prompt.trim()}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating ? (
            <>
              <span className="inline-block animate-spin mr-2">⏳</span>
              Generating...
            </>
          ) : (
            '✨ Generate Event'
          )}
        </button>
      </div>

      {/* Error Display */}
      {generationError && (
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <strong>Error:</strong> {generationError}
        </div>
      )}

      {/* Generated Event Preview */}
      {generatedEvent && (
        <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <h2 className="text-2xl font-bold mb-2">{generatedEvent.title}</h2>
            {generatedEvent.subtitle && (
              <p className="text-blue-100">{generatedEvent.subtitle}</p>
            )}
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Quick Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-semibold capitalize">{generatedEvent.event_type}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Format</p>
                <p className="font-semibold capitalize">{generatedEvent.format}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Capacity</p>
                <p className="font-semibold">{generatedEvent.capacity || 'TBD'}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: generatedEvent.description }}
              />
            </div>

            {/* Location */}
            <div>
              <h3 className="font-semibold mb-2">📍 Location</h3>
              <p className="font-medium">{generatedEvent.location.name}</p>
              <p className="text-sm text-gray-600">
                {generatedEvent.location.address}
                <br />
                {generatedEvent.location.city}, {generatedEvent.location.country}
              </p>
            </div>

            {/* Tags */}
            {generatedEvent.tags && generatedEvent.tags.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">🏷️ Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {generatedEvent.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Success Message */}
            {savedEventId && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg">
                ✅ Event created successfully!
                <button
                  onClick={() => navigate(`/events/${savedEventId}`)}
                  className="ml-4 text-green-800 underline"
                >
                  View Event →
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={clear}
                className="flex-1 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
              {savedEventId && (
                <button
                  onClick={() => navigate(`/events/${savedEventId}`)}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                  Go to Event
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventGeneratorPage;
